/*
 * The agent run tree, as state.
 *
 * WHAT THIS IS FOR. `spawn_subagent` has been a real tool for a while: the model can start a child
 * run, the conversation DO records it, leases keep exactly one driver advancing it, and a digest is
 * rendered for the orchestrator to read. Every part of that worked and NONE of it was ever on
 * screen. A user asking for something that fans out saw a tool row and then silence, which is the
 * same defect class as the reasoning tokens — real work happening, invisible.
 *
 * Manager-pattern, like everything else that holds state here: it does the IO, keeps the tree, and
 * emits `change`. Views subscribe and read; they never fetch (the Cardinal Rule).
 *
 * ONE READ, NOT FIVE. `TreeView` is a deliberately wide verb — runs, digest, dangling tool calls,
 * the stale path, pending steers — because the alternative is a view issuing one request per
 * question and a tree of twenty nodes costing twenty round trips. This asks for what it renders in
 * a single call.
 */
import { create, toBinary, fromBinary } from '@bufbuild/protobuf';
import { TreeViewRequestSchema, TreeViewReplySchema, OwnerActionSchema, OwnerActionReplySchema, OwnerAction_Kind, RunStatus, } from '@vitesse/proto/vitesse/ai/v1/agent_pb.js';
import { stopLocalSteps } from '@vitesse/agent-loop';
import { cancelAsk } from './ask-user';
import { treeHost } from './driver';
/**
 * Every run in the tree that contains `runId` — root or not — with each finished run's stored
 * return. One read, not a poll: for a tool that needs what a sibling or child handed back (a widget
 * placed by the name of the agent that wrote it), the tree is the record and the DO resolves any
 * run to its root.
 */
export async function treeRunsOf(runId) {
    const bytes = await treeHost().treeView(toBinary(TreeViewRequestSchema, create(TreeViewRequestSchema, { rootRunId: runId, wantRuns: true })));
    return fromBinary(TreeViewReplySchema, bytes).runs;
}
/**
 * The peer-message edges the store reported, plus anything still sitting in the inbox.
 *
 * TWO SOURCES, ONE LIST, and the second exists to close a gap of one poll. The store's edges are the
 * durable record — every exchange, read or not — and they are what makes a canvas show that two
 * agents talked at all. The inbox is what the driver has not yet folded in, and it is fresher: a
 * message sent a hundred milliseconds ago is in the inbox before the edge query that would collapse
 * it into a pair has been asked. Merging them means an exchange lights up immediately and then stays
 * drawn, rather than appearing for one poll and disappearing forever.
 *
 * Pure, and exported, because the merging and the FILTERING are the parts with decisions in them.
 * The filters, each of which draws a wrong picture if missed:
 *
 *   · no from_run_id — a PERSON steering the run. Not a link between two agents, and drawing it
 *     would put an edge on the canvas coming from nowhere.
 *   · from === to — a run messaging itself. Legal, and a self-loop says nothing a reader can use.
 *   · duplicates — an unread message is in BOTH sources by construction. It is one edge, and it is
 *     pending if either source says so.
 */
export function meshEdges(injections, edges = []) {
    const byPair = new Map();
    const add = (from, to, pending) => {
        if (!from || !to || from === to)
            return;
        const key = `${from}>${to}`;
        const had = byPair.get(key);
        if (had)
            had.pending = had.pending || pending;
        else
            byPair.set(key, { from, to, pending });
    };
    for (const e of edges)
        add(e.fromRunId, e.toRunId, e.pending);
    // The inbox always means unread — that is what being in it is.
    for (const inj of injections)
        add(inj.fromRunId, inj.runId, true);
    return [...byPair.values()];
}
/** The header's tally, over the runs this view holds. Every run is in exactly one bucket, so the
 *  four always sum to the total — a header that does not add up reads as a rendering fault. */
export function countOf(runs) {
    const c = { total: runs.length, running: 0, awaiting: 0, queued: 0, finished: 0 };
    for (const r of runs) {
        if (r.status === RunStatus.RUNNING)
            c.running += 1;
        else if (r.status === RunStatus.AWAITING)
            c.awaiting += 1;
        else if (r.status === RunStatus.QUEUED)
            c.queued += 1;
        else if (r.status === RunStatus.FINISHED)
            c.finished += 1;
    }
    return c;
}
/** The run to READ a node's state from — its newest attempt, else the run itself. */
export function latestAttempt(node) {
    return node.attempts.length ? node.attempts[node.attempts.length - 1] : node.run;
}
/**
 * How often a live tree is re-read.
 *
 * POLLED, not pushed, and that is a deliberate first cut rather than an oversight. The runs live in
 * a per-user Durable Object which already has a socket for conversations; adding tree frames to it
 * is the right end state and a bigger change than this. A 2s poll is well inside the rate a headline
 * changes (a step takes seconds at best) and stops entirely when nothing is running, so an idle tree
 * costs nothing.
 */
const POLL_MS = 2000;
export class RunTree extends EventTarget {
    constructor() {
        super(...arguments);
        this._roots = [];
        this._byId = new Map();
        this._digest = '';
        this._counts = { total: 0, running: 0, awaiting: 0, queued: 0, finished: 0 };
        this._error = '';
        this._loading = false;
        this.timer = null;
        this._notes = [];
        this.rootRunId = '';
        /** Set while a read is in flight, so a slow response cannot overlap the next tick. */
        this.inFlight = false;
        /** A refresh asked for while a read was in flight. Served once, on the way out of that read. */
        this.again = false;
        /** Consecutive failed polls, for the backoff. */
        this.failures = 0;
    }
    /** The forest, parents before children. Usually one root; a conversation that spawned twice has two. */
    get roots() {
        return this._roots;
    }
    /** One run per agent — each node's newest attempt — in tree order. */
    agents() {
        const out = [];
        const walk = (n) => {
            out.push(latestAttempt(n));
            n.children.forEach(walk);
        };
        this._roots.forEach(walk);
        return out;
    }
    /** The model-facing summary, verbatim. The same string the orchestrator reads, which makes it
     *  worth showing somewhere: when a user and an agent disagree about what the tree is doing, this
     *  is the text the agent was actually given. */
    get digest() {
        return this._digest;
    }
    /*
     * COUNTED OVER WHAT THIS VIEW ACTUALLY HOLDS.
     *
     * These were read off the digest, on the reasoning that a header saying "3 running" must not
     * disagree with the summary the model was handed. That stopped being the same number the moment
     * the canvas started asking for the FOREST: the digest is per-tree on purpose — it is what a
     * driver reasons over, and widening it would put four unrelated fan-outs into one agent's context
     * — so a conversation that spawned two agents in one turn drew two cards under a badge that said
     * one agent. The header describes the picture; the picture is the forest.
     *
     * The digest's PROSE is still passed through untouched, because that one genuinely is the model's
     * summary and its value is being verbatim.
     */
    get counts() {
        return this._counts;
    }
    /** Prose, never a code — the platform rule. Empty when the last read succeeded. */
    get error() {
        return this._error;
    }
    /** True only for the FIRST read. A refresh must not blank a tree that is already on screen. */
    get loading() {
        return this._loading;
    }
    /** Every run in the tree, flat, in the order the lanes render. */
    get flat() {
        const out = [];
        const walk = (n) => {
            out.push(n.run);
            n.children.forEach(walk);
        };
        this._roots.forEach(walk);
        return out;
    }
    /** Agent-to-agent notes currently in flight, as from→to pairs. The mesh: these edges cross the
     *  spawn tree in any direction, which is the whole reason a tree view alone under-describes what a
     *  fleet of agents is doing. */
    get notes() {
        return this._notes;
    }
    /** Is anything still moving? Drives whether to keep polling and whether to show live affordances. */
    get active() {
        // 4 is RUN_STATUS_FINISHED; anything else is queued, running or awaiting.
        return this.flat.some((r) => r.status !== 4);
    }
    /** What the whole tree has cost, rolled up the spawn edge. Absent — not zero — when no run
     *  reported a figure, so "free so far" and "nobody said" stay distinguishable. */
    get costUsd() {
        const roots = this._roots.map((n) => n.run.subtreeUsage?.costUsd).filter((c) => typeof c === 'number');
        return roots.length ? roots.reduce((a, b) => a + b, 0) : undefined;
    }
    /** Follow one tree. Safe to call repeatedly with the same id — it will not restart the poll. */
    watch(rootRunId) {
        if (rootRunId === this.rootRunId)
            return;
        this.stop();
        this.rootRunId = rootRunId;
        if (!rootRunId)
            return;
        this._loading = true;
        this.emit();
        void this.tick();
    }
    /** Stop following. Called when the view goes away — a poll outliving its view is a leak that only
     *  shows up as unexplained traffic. */
    stop() {
        if (this.timer)
            clearTimeout(this.timer);
        this.timer = null;
        this.rootRunId = '';
        this.inFlight = false;
    }
    /**
     * Read once, now — for a view that wants to refresh on demand rather than wait for the tick.
     *
     * COALESCED RATHER THAN DROPPED when a poll is already running. `tick` refuses to overlap itself,
     * which is right, but a plain refusal loses the request — and the caller that most needs this is
     * the driver saying "a run just ended", where the in-flight read was very likely issued BEFORE
     * the write it is asking about. Dropping it means the ending waits for the next scheduled poll,
     * which is the two-second delay this call exists to avoid.
     *
     * So the request is remembered and served the moment the current read lands. One extra read at
     * most, however many callers ask.
     */
    async refresh() {
        if (this.inFlight) {
            this.again = true;
            return;
        }
        await this.tick(false);
    }
    async tick(reschedule = true) {
        if (this.inFlight || !this.rootRunId)
            return;
        this.inFlight = true;
        this.again = false;
        try {
            const bytes = await treeHost().treeView(toBinary(TreeViewRequestSchema, create(TreeViewRequestSchema, {
                rootRunId: this.rootRunId,
                wantRuns: true,
                /*
                 * THE FOREST, not the one tree this id happens to name.
                 *
                 * A sub-agent spawned from a chat turn is a root of its own, and each turn has its own
                 * synthetic parent id — so a conversation that spawned four agents across four turns
                 * produced four separate trees and the canvas drew one of them, while the recents list
                 * (reading the same rows a different way) listed all four. The canvas's subject is the
                 * CONVERSATION's fan-out, so it asks for the conversation's fan-out.
                 */
                wantForest: true,
                wantDigest: true,
                wantStale: true,
                /* Agent-to-agent notes. A pending injection carries the run it landed in and the run that
                   WROTE it, so one with a from_run_id is a message edge — the only record of agents
                   talking to each other directly rather than through the spawn tree. sinceSeq 0 is a
                   full snapshot: this is a picture of what is in flight, not a tail. */
                wantInjections: true,
                injectionsSinceSeq: 0n,
                /* And the DURABLE record of who has spoken to whom. The inbox above is only what is
                   still unread — derived from it alone, an edge appeared for a poll or two and then
                   vanished the moment the recipient read it, so every exchange that mattered was gone
                   by the time anyone looked. Together: it lights up immediately and then stays drawn. */
                wantMessages: true,
            })));
            const reply = fromBinary(TreeViewReplySchema, bytes);
            // The universal envelope: a refusal is prose, and it is shown rather than swallowed. A tree
            // that silently stops updating is indistinguishable from a tree where nothing is happening.
            this._error = reply.result?.error ? (reply.result.summary ?? 'the run tree could not be read') : '';
            this._digest = reply.digest?.text ?? '';
            this.index(reply.runs);
            // Over AGENTS, not attempts: a woken author is one agent in the header as it is one card
            // on the canvas. `active` below still watches every run, so a queued wake keeps the poll alive.
            this._counts = countOf(this.agents());
            this._notes = meshEdges(reply.injections, reply.messages);
            this.failures = 0;
        }
        catch (e) {
            this._error = e instanceof Error ? e.message : String(e);
            // What was on screen stays on screen: the runs already indexed are the last truth we had, and a
            // failed read says nothing about them. Only the next poll is pushed out.
            this.failures = Math.min(this.failures + 1, 6);
        }
        finally {
            this.inFlight = false;
            this._loading = false;
            this.emit();
            // Somebody asked for a read while this one was running — most likely a driver reporting that a
            // run just ended, whose write may well have landed after this read was issued. Serve it now
            // rather than making the ending wait out a poll interval.
            if (this.again && this.rootRunId) {
                this.again = false;
                void this.tick(false);
            }
            // Keep polling only while something can still change. A finished tree is a document.
            // Backed off while the service is not answering: a worker restarting, a proxy with nothing
            // behind it. The first retry is quick (half a second — an outage that short should not even be
            // noticed), then each failure doubles the wait up to a minute, and a success resets it.
            if (reschedule && this.rootRunId && this.active)
                this.timer = setTimeout(() => void this.tick(), this.failures === 0 ? POLL_MS : Math.min(500 * 2 ** (this.failures - 1), 60_000));
        }
    }
    /**
     * Build the forest from a flat list.
     *
     * Parented by `parent_run_id`, and a node whose parent is not in the reply is treated as a ROOT
     * rather than dropped. A partial read is normal — a page of a large tree, or a child recorded
     * before its parent lands — and silently discarding those nodes would show a user fewer agents
     * than are running, which is the one thing this view must not do.
     */
    index(runs) {
        this._byId = new Map(runs.map((r) => [r.runId, r]));
        const nodes = new Map(runs.map((r) => [r.runId, { run: r, children: [], attempts: [] }]));
        const roots = [];
        // A continuation folds onto the node it continues, following the chain to its first attempt so
        // a third wake lands on the same card as the second. Only when that first attempt is in the
        // reply — otherwise it stands on its own, for the same reason an orphan is a root, not a drop.
        const origin = (run) => {
            let cur = run;
            let found;
            for (let hops = 0; cur?.continuesRunId && hops < 64; hops++) {
                const prev = nodes.get(cur.continuesRunId);
                if (!prev)
                    break;
                found = prev;
                cur = prev.run;
            }
            return found;
        };
        for (const run of runs) {
            const node = nodes.get(run.runId);
            const first = origin(run);
            if (first) {
                first.attempts.push(run);
                continue;
            }
            const parent = run.parentRunId ? nodes.get(run.parentRunId) : undefined;
            if (parent)
                parent.children.push(node);
            else
                roots.push(node);
        }
        for (const n of nodes.values())
            n.attempts.sort((a, b) => (a.startedAtNs < b.startedAtNs ? -1 : a.startedAtNs > b.startedAtNs ? 1 : 0));
        // Oldest first, so a lane's order is the order work was started and does not jump as the tree
        // grows. `started_at_ns` is an int64 and arrives as a bigint — comparing it as a number would
        // lose precision on a nanosecond timestamp.
        const bySpawn = (a, b) => a.run.startedAtNs < b.run.startedAtNs ? -1 : a.run.startedAtNs > b.run.startedAtNs ? 1 : 0;
        const sort = (n) => {
            n.children.sort(bySpawn);
            n.children.forEach(sort);
        };
        roots.sort(bySpawn);
        roots.forEach(sort);
        this._roots = roots;
    }
    /** One run by id — what a view needs to open a node's own conversation. */
    /* ── THE OWNER'S CONTROLS ───────────────────────────────────────────────────────────────────
     *
     * A tree could be watched and not touched. Everything below existed on the server, tested, and
     * had no way to be reached from a browser: the only caller of `/agent/owner` in the whole
     * codebase was a test. So "stop this branch", "stop everything" and "try that again" were
     * features the product did not have, and a panel built on top of a read-only view would have had
     * buttons wired to nothing.
     *
     * THESE ARE THE OWNER'S VERBS, and they present NO EPOCH. Every other mutation of a tree carries
     * the fence of the lease its caller holds, because the lease stops two DRIVERS fighting over one
     * tree. The user is not a driver — they are the owner — and an owner action refused because some
     * background tab holds a lease is our bookkeeping surfacing as a broken button.
     *
     * Each returns the refusal in prose, or '' on success, so a view can say what happened instead of
     * silently doing nothing. The tree is re-read immediately rather than waiting for the next poll:
     * a stop that takes two seconds to show up reads as a stop that did not work, and the second
     * press is the one that matters.
     */
    /** Stop ONE branch and everything under it. The reason is the user's own words and lands on the
     *  record, which is what a person reading the tree an hour later has to go on. */
    async abort(runId, reason) {
        return this.owner(OwnerAction_Kind.ABORT, { runId, reason });
    }
    /** THE STOP BUTTON: every live run this user has, across every tree. Deliberately not scoped to
     *  the tree being watched — a person pressing stop means stop, and having to find the other tab
     *  is not an answer. */
    async haltAll(reason) {
        return this.owner(OwnerAction_Kind.HALT_ALL, { reason });
    }
    /** Mark a run's answer stale, so the wave re-runs what depended on it. */
    async invalidate(runId, reason) {
        return this.owner(OwnerAction_Kind.INVALIDATE, { runId, reason, invalidate: { reason } });
    }
    async owner(kind, fields) {
        try {
            const reply = fromBinary(OwnerActionReplySchema, await treeHost().ownerAct(toBinary(OwnerActionSchema, create(OwnerActionSchema, { kind, ...fields }))));
            if (reply.result?.error)
                return reply.result.summary || reply.result.error.explanation || 'that could not be done';
            // The record says stopped. The step this tab is taking for any of those runs is cut off now:
            // its model call ends, and the tools it was about to name do not run. And what any of them
            // was waiting on a person for closes with it — the whole subtree, not only the run named: a
            // child's question left open after its parent was stopped restarted work when answered.
            if (kind === OwnerAction_Kind.ABORT || kind === OwnerAction_Kind.HALT_ALL) {
                stopLocalSteps(reply.affectedRunIds);
                for (const id of reply.affectedRunIds)
                    cancelAsk(id);
                this.dispatchEvent(new CustomEvent('stopped', { detail: [...reply.affectedRunIds] }));
            }
            await this.refresh();
            return '';
        }
        catch (e) {
            // A transport failure is NOT a refusal, and saying so matters: one is the server declining,
            // the other is the request never arriving, and only the second is worth retrying blindly.
            return e instanceof Error ? e.message : String(e);
        }
    }
    run(runId) {
        return this._byId.get(runId);
    }
    /**
     * HOW FAR DOWN A RUN SITS — 0 for a root, 1 for what a root spawned, and so on.
     *
     * Walked from the forest rather than read off the run, because a run carries its ROOT and its
     * parent, not its distance from the top, and the panel needs the distance: it orders the columns
     * so they read as the hierarchy they are. A run this tree has never seen answers -1, which sorts
     * before everything and is the honest answer for "I do not know where this belongs".
     */
    depthOf(runId) {
        if (!runId)
            return -1;
        const find = (n, d) => {
            if (n.run.runId === runId || n.attempts.some((a) => a.runId === runId))
                return d;
            for (const c of n.children) {
                const hit = find(c, d + 1);
                if (hit >= 0)
                    return hit;
            }
            return -1;
        };
        for (const r of this._roots) {
            const hit = find(r, 0);
            if (hit >= 0)
                return hit;
        }
        return -1;
    }
    emit() {
        this.dispatchEvent(new CustomEvent('change'));
    }
}
/**
 * THE PERSON ENDS A WAIT EARLY — the kill button on a waiting tool row.
 *
 * Not a method of a tree, because the row that offers it belongs to a THREAD, and a thread knows its
 * own conversation and the call id but not necessarily which tree it is watching. The server finds
 * the run from the conversation, writes "cancelled" into that same call, and the agent reads it at
 * its next step — so the row updates through the ordinary transcript stream, with nothing to refresh.
 *
 * Returns the refusal in prose ("that wait has already ended"), or '' when it was cancelled.
 */
export async function cancelWait(conversationId, toolCallId) {
    try {
        const reply = fromBinary(OwnerActionReplySchema, await treeHost().ownerAct(toBinary(OwnerActionSchema, create(OwnerActionSchema, { kind: OwnerAction_Kind.CANCEL_WAIT, conversationId, toolCallId }))));
        if (reply.result?.error)
            return reply.result.summary || reply.result.error.explanation || 'that could not be done';
        return '';
    }
    catch (e) {
        return e instanceof Error ? e.message : String(e);
    }
}
//# sourceMappingURL=run-tree.js.map